import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  MapPin,
  Grid3X3,
  List,
  Search,
  Filter,
  DollarSign,
  Home,
  Calendar,
  RefreshCw,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { PortfolioMap } from "@/components/portfolio/PortfolioMap";
import { PortfolioList } from "@/components/portfolio/PortfolioList";
import { PortfolioStats } from "@/components/portfolio/PortfolioStats";
import { toast } from "sonner";

export interface CompletedJob {
  id: string;
  job_number: string;
  owner_name: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  latitude: number;
  longitude: number;
  roof_type: string;
  estimated_value: number;
  status: string;
  updated_at: string;
  created_at: string;
  photo_url?: string;
  photos?: { url: string }[];
}

export default function Portfolio() {
  const { orgId } = useAuth();
  const [jobs, setJobs] = useState<CompletedJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"map" | "grid">("map");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [materialFilter, setMaterialFilter] = useState("all");
  const [selectedJob, setSelectedJob] = useState<CompletedJob | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (orgId) {
      fetchCompletedJobs();
    }
  }, [orgId, dateFilter, materialFilter]);

  const fetchCompletedJobs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("jobs")
        .select(`
          id,
          job_number,
          owner_name,
          property_address,
          property_city,
          property_state,
          property_zip,
          latitude,
          longitude,
          roof_type,
          estimated_value,
          status,
          updated_at,
          created_at
        `)
        .eq("org_id", orgId)
        .or("status.ilike.%complete%,status.ilike.%closed_won%");

      // Apply date filter
      if (dateFilter !== "all") {
        const now = new Date();
        let startDate: Date;

        switch (dateFilter) {
          case "6months":
            startDate = new Date(now.setMonth(now.getMonth() - 6));
            break;
          case "year":
            startDate = new Date(now.setFullYear(now.getFullYear() - 1));
            break;
          case "2years":
            startDate = new Date(now.setFullYear(now.getFullYear() - 2));
            break;
          default:
            startDate = new Date(0);
        }

        query = query.gte("updated_at", startDate.toISOString());
      }

      // Apply material filter
      if (materialFilter !== "all") {
        query = query.ilike("roof_type", `%${materialFilter}%`);
      }

      const { data: jobsData, error } = await query.order("updated_at", { ascending: false });

      if (error) throw error;

      // Fetch photos for each job
      const jobIds = (jobsData || []).map((j) => j.id);
      let photosMap: Record<string, string> = {};

      if (jobIds.length > 0) {
        const { data: photosData } = await supabase
          .from("photos")
          .select("job_id, url")
          .in("job_id", jobIds)
          .order("created_at", { ascending: false });

        for (const photo of photosData || []) {
          if (!photosMap[photo.job_id]) {
            photosMap[photo.job_id] = photo.url;
          }
        }
      }

      const jobsWithPhotos = (jobsData || []).map((job) => ({
        ...job,
        photo_url: photosMap[job.id],
      }));

      setJobs(jobsWithPhotos);
    } catch (error: any) {
      console.error("Error fetching completed jobs:", error);
      toast.error("Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToProjectMapIt = async () => {
    setSyncing(true);
    try {
      // Call edge function to sync all jobs
      const { data, error } = await supabase.functions.invoke("project-mapit-sync", {
        body: {
          action: "bulk_sync",
          orgId,
          jobs: jobs.map((j) => ({
            id: j.id,
            address: j.property_address,
            city: j.property_city,
            state: j.property_state,
            lat: j.latitude,
            lng: j.longitude,
            material: j.roof_type,
            value: j.estimated_value,
          })),
        },
      });

      if (error) throw error;

      toast.success("Portfolio synced to Project Map It!", {
        description: `${jobs.length} projects synced successfully`,
      });
    } catch (error: any) {
      console.error("Error syncing to PMI:", error);
      toast.error("Failed to sync with Project Map It");
    } finally {
      setSyncing(false);
    }
  };

  // Filter jobs by search query
  const filteredJobs = jobs.filter((job) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      job.owner_name?.toLowerCase().includes(query) ||
      job.property_address?.toLowerCase().includes(query) ||
      job.property_city?.toLowerCase().includes(query) ||
      job.job_number?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const totalValue = jobs.reduce((sum, j) => sum + (j.estimated_value || 0), 0);
  const uniqueCities = new Set(jobs.map((j) => j.property_city)).size;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b bg-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-green-500">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Portfolio Map</h1>
              <p className="text-muted-foreground">
                Visual showcase of all your completed projects
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSyncToProjectMapIt}
              disabled={syncing || jobs.length === 0}
              className="gap-2"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync with Project Map It
            </Button>
          </div>
        </div>

        {/* Stats */}
        <PortfolioStats
          totalJobs={jobs.length}
          totalValue={totalValue}
          citiesCovered={uniqueCities}
        />
      </div>

      {/* Filters */}
      <div className="p-4 border-b bg-muted/30 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, address, or job #..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={dateFilter} onValueChange={setDateFilter}>
          <SelectTrigger className="w-[150px]">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Time</SelectItem>
            <SelectItem value="6months">Last 6 Months</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="2years">Last 2 Years</SelectItem>
          </SelectContent>
        </Select>

        <Select value={materialFilter} onValueChange={setMaterialFilter}>
          <SelectTrigger className="w-[150px]">
            <Home className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Material" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Materials</SelectItem>
            <SelectItem value="shingle">Shingle</SelectItem>
            <SelectItem value="metal">Metal</SelectItem>
            <SelectItem value="tile">Tile</SelectItem>
            <SelectItem value="flat">Flat/TPO</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant={viewMode === "map" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("map")}
          >
            <MapPin className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : viewMode === "map" ? (
          <PortfolioMap
            jobs={filteredJobs}
            selectedJob={selectedJob}
            onSelectJob={setSelectedJob}
          />
        ) : (
          <ScrollArea className="h-full">
            <PortfolioList
              jobs={filteredJobs}
              onSelectJob={setSelectedJob}
            />
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
